import React, { useCallback, useEffect, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Flex,
    Highlight,
    Menu,
    Notification,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { inject, observer } from 'mobx-react';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import {
    IconAlarmPlus,
    IconArrowRight,
    IconCalendar,
    IconCalendarCheck,
    IconCalendarDue,
    IconCirclesRelation,
    IconDotsVertical,
    IconDownload,
    IconEye,
    IconPlus,
    IconRepeat,
    IconSearch,
    IconTrash,
    IconTruckDelivery,
    IconUser,
} from '@tabler/icons-react';
import { containsStringIgnoreCase } from './PatientTable.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { useGetTasks } from '../../../global/entities/Tasks/queries/useGetTasks.tsx';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useDeleteTaskMutations } from '../../../global/entities/Tasks/mutations/useDeleteTaskMutations.tsx';
import { Task } from '../../../global/entities/Tasks/Tasks.ts';
import { theme } from '../../../global/style/theme.tsx';
import Utils from '../../../global/helpers/utils.ts';
import {
    useAssistantTaskReminder,
    useClientTaskReminder,
} from '../../../global/entities/Tasks/mutations/useTaskReminderMutations.tsx';
import { TasksApi } from '../../../global/apis/TasksApi/TasksApi.ts';
import { handleTrackingClick } from '../../../global/helpers/trackingUtils.ts';
import { useGetTasksByPatient } from '../../../global/entities/Tasks/queries/useGetTasksByPatient.tsx';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { TaskType } from '../../../global/types';
import { SurveyPdfService, type ISurveyDataForPdf } from '../../../services/surveyPdfService';
import { getCurrentLanguage } from '../../../global/helpers/trackingUtils';
import { QuestionnaireAnswerApi } from '../../../global/apis/QuestionnaireAnswerApi/QuestionnaireAnswerApi.ts';
import { notifications } from '@mantine/notifications';
import { useEmailErrorHandler } from '../../../global/hooks/useEmailErrorHandler.ts';

export type ITaskTableProps = {
    routeStore?: RouteStore;
};

const TaskTable: React.FC<ITaskTableProps> = inject(
    'routeStore',
    'patientStore',
)(
    observer(({ routeStore }) => {
        const { data: patientTasks, isLoading: isLoadingTasksByPatient } = useGetTasksByPatient();
        const { data: tasks, isLoading: isLoadingTasks } = useGetTasks();

        const sendReminderToUser = useClientTaskReminder();
        const sendReminderToAssistant = useAssistantTaskReminder();
        const navigate = useNavigate();
        const deleteTask = useDeleteTaskMutations();
        const [search, setSearch] = useState('');
        const [pdfDownloadingTaskId, setPdfDownloadingTaskId] = useState<number | null>(null);
        const { ref, width } = useElementSize();
        const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } = useTable();
        const { handleEmailError } = useEmailErrorHandler();

        // Add media query hooks to detect small portrait screens
        const isSmallScreen = useMediaQuery('(max-width: 767px)');
        const isPortrait = useMediaQuery('(orientation: portrait)');
        const isTouchDevice = useMediaQuery('(pointer: coarse)');

        useEffect(() => {
            routeStore?.setFromLocation();
        }, []);

        useEffect(() => {
            if (location.pathname.includes('hcpPatientDashboard')) 
                setSortedData(patientTasks || []);
             else 
                setSortedData(tasks || []);
            
        }, [tasks, patientTasks, setSortedData]);

        const onClickTask = (id: number) => {
            navigate(`../task/${id}`);
        };

        const openResult = (id: number, taskTypeID: TaskType) => {
            switch (taskTypeID) {
                case TaskType.questionnaire:
                    navigate(`../surveyResult/${id}`);
                    return;
            }
        };

        const downloadSurveyPdf = async (taskId: number, surveyTitle?: string) => {
            try {
                setPdfDownloadingTaskId(taskId);
                
                // Fetch questionnaire answer data using the task ID
                const questionnaireData = await QuestionnaireAnswerApi.getQuestionnaireAnswerByTaskId(taskId.toString());
                
                if (!questionnaireData?.questionnaireJson) {
                    notifications.show({
                        title: t('error.title', 'Error'),
                        message: t('error.questionnaire.notFound', 'Questionnaire data not found'),
                        color: 'red',
                    });
                    return;
                }

                // Prepare survey data for PDF generation
                const surveyData: ISurveyDataForPdf = {
                    questionnaireJson: questionnaireData.questionnaireJson,
                    answersJson: questionnaireData.answersJson,
                    locale: getCurrentLanguage(), // Use current user's language preference
                };

                // Generate and download PDF
                const filename = SurveyPdfService.generateFilename(taskId, surveyTitle);
                await SurveyPdfService.downloadPdf(surveyData, filename);

                notifications.show({
                    title: t('success.title', 'Success'),
                    message: t('success.pdfDownloaded', 'PDF downloaded successfully'),
                    color: 'green',
                });

            } catch (error: any) {
                console.error('Error downloading survey PDF:', error);
                notifications.show({
                    title: t('error.title', 'Error'),
                    message: t('error.pdfDownload', 'Failed to download PDF. Please try again.'),
                    color: 'red',
                });
            } finally {
                setPdfDownloadingTaskId(null);
            }
        };

        const openModalRappel = async (task: Task) => {
            const hasAssistant = await TasksApi.getHasAssistant(task.id!.toString());

            const handleSendReminderToUser = async () => {
                try {
                    await sendReminderToUser.mutateAsync({ taskId: task.id!.toString() });
                    modals.closeAll();
                } catch (error: any) {
                    const emailError = handleEmailError(error);
                    const errorMessage = emailError?.message || error?.response?.data?.message || error?.message;
                    const errorTitle = emailError?.title || error?.response?.data?.title || error?.title;
                    
                    // Close current modal and open error modal
                    modals.closeAll();
                    modals.open({
                        title: errorTitle,
                        children: (
                            <Stack gap={'md'}>
                                <Notification color="red" withCloseButton={false}>
                                    {errorMessage}
                                </Notification>
                                <Button onClick={() => modals.closeAll()}>
                                    {t('button.close')}
                                </Button>
                            </Stack>
                        ),
                        size: 'sm',
                    });
                }
            };

            const handleSendReminderToAssistant = async () => {
                try {
                    await sendReminderToAssistant.mutateAsync({ taskId: task.id!.toString() });
                    modals.closeAll();
                } catch (error: any) {
                    const emailError = handleEmailError(error);
                    const errorMessage = emailError?.message || error?.response?.data?.message || error?.message;
                    const errorTitle = emailError?.title || error?.response?.data?.title || error?.title;

                    // Close current modal and open error modal
                    modals.closeAll();
                    modals.open({
                        title: errorTitle,
                        children: (
                            <Stack gap={'md'}>
                                <Notification color="red" withCloseButton={false}>
                                    {errorMessage}
                                </Notification>
                                <Button onClick={() => modals.closeAll()}>
                                    {t('button.close')}
                                </Button>
                            </Stack>
                        ),
                        size: 'sm',
                    });
                }
            };

            return modals.openConfirmModal({
                title: t('modal.sendReminderModal.sendReminder'),
                children: (
                    <Stack gap={'md'}>
                        <TextInput
                            label={t('global.patientName')}
                            leftSection={<IconUser size={16} />}
                            value={task.patientName}
                            disabled
                        />
                        <TextInput
                            label={t('formProfile.birthDate')}
                            description={'YYYY-MM-DD'}
                            leftSection={<IconCirclesRelation size={16} />}
                            value={
                                task.patientBirthday ? new Date(task.patientBirthday).toISOString().split('T')[0] : ''
                            }
                            disabled
                        />
                        <Button
                            variant={'filled'}
                            color={'blue'}
                            onClick={handleSendReminderToUser}
                        >
                            {t('modal.sendReminderModal.sendReminder')}
                        </Button>
                    </Stack>
                ),
                labels: { cancel: t('modal.sendReminderModal.delegateToAssistant'), confirm: t('button.cancel') },
                cancelProps: { variant: 'filled', w: '100%', color: 'blue', disabled: !hasAssistant },
                confirmProps: { variant: 'outline', w: '100%', color: 'gray' },
                onCancel: handleSendReminderToAssistant,
                onConfirm: () => modals.closeAll(),
                size: 'sm',
            });
        };

        const openModalDelete = (taskId: string) =>
            modals.openConfirmModal({
                title: t('modal.deleteTaskModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteTaskModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteTaskModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => onClickDelete(taskId),
                size: 'lg',
                radius: 'xs',
            });

        const onClickDelete = useCallback(
            (taskId: string) => {
                deleteTask.mutateAsync(taskId.toString());
            },
            [deleteTask],
        );

        const isMobilePortrait = isSmallScreen && isPortrait && isTouchDevice;

        if (isLoadingTasksByPatient || isLoadingTasks)
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        const isPatientDashboard = location.pathname.includes('hcpPatientDashboard');

        const filteredTasks = sortedData?.filter((task: Task) =>
            containsStringIgnoreCase([`${task.patientName}`, `${task.description}`], search) || !search
        );

        const renderTaskActions = (task: Task) => (
            <Flex gap={4} align="center" style={{ flexShrink: 0 }}>
                {task.taskTypeID === TaskType.questionnaire && task.completionDate !== null && (
                    <Tooltip label={t('toolTip.seeResult')}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            onClick={(e) => { e.stopPropagation(); openResult(task.id, task.taskTypeID); }}
                        >
                            <IconEye size={18} />
                        </ActionIcon>
                    </Tooltip>
                )}
                <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
                            <IconDotsVertical size={18} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {task.completionDate === null && (
                            <Menu.Item
                                leftSection={<IconAlarmPlus size={16} color="teal" />}
                                onClick={() => openModalRappel(task)}
                            >
                                {t('toolTip.sendReminder')}
                            </Menu.Item>
                        )}
                        {task.taskTypeID === TaskType.questionnaire && task.completionDate !== null && (
                            <Menu.Item
                                leftSection={<IconDownload size={16} color="var(--mantine-color-blue-6)" />}
                                onClick={() => downloadSurveyPdf(task.id, task.description)}
                            >
                                {t('toolTip.downloadPdf', 'Download PDF')}
                            </Menu.Item>
                        )}
                        {task.trackingNumber && (
                            <Menu.Item
                                leftSection={<IconTruckDelivery size={16} color="var(--mantine-color-blue-6)" />}
                                onClick={(e) => handleTrackingClick(task.trackingNumber, e)}
                            >
                                {t('toolTip.shipmentTracking')}
                            </Menu.Item>
                        )}
                        {task.completionDate === null && (
                            <>
                                <Menu.Divider />
                                <Menu.Item
                                    leftSection={<IconTrash size={16} color="var(--mantine-color-red-6)" />}
                                    color="red"
                                    onClick={() => openModalDelete(task.id!.toString())}
                                >
                                    {t('toolTip.delete')}
                                </Menu.Item>
                            </>
                        )}
                    </Menu.Dropdown>
                </Menu>
                <ActionIcon
                    variant="transparent"
                    onClick={(e) => { e.stopPropagation(); onClickTask(task.id); }}
                >
                    <IconArrowRight color={theme.colors?.blue?.toString()} />
                </ActionIcon>
            </Flex>
        );
        
        return (
            <Stack gap={'lg'} pt={isPatientDashboard ? 0 : 'xl'} h={'100%'}>
                <Flex hidden={!location.pathname.includes('hcpDashboard')} align={'center'} justify={'space-between'}>
                    <Title c="black" order={3}>
                        {t('page.task.task')}
                    </Title>
                </Flex>
                <Paper w="100%" ref={ref}>
                    <Stack gap={'md'} h={'100%'} ref={ref}>
                        <Flex align={'center'} gap={'sm'}>
                            <TextInput
                                hidden={isMobilePortrait}
                                placeholder={t('global.search')}
                                rightSection={isMobilePortrait ? null : <IconSearch />}
                                value={search}
                                color={'var(--mantine-color-default-color)'}
                                style={{ flex: 1 }}
                                radius={'md'}
                                onChange={(el) => setSearch(el.currentTarget.value)}
                            />
                            <Button
                                variant="filled"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => onClickTask(0)}
                                w={isMobilePortrait ? '100%' : undefined}
                            >
                                {t('page.task.addTask')}
                            </Button>
                        </Flex>

                        {isMobilePortrait ? (
                            /* Mobile card view */
                            <Stack gap="xs">
                                {filteredTasks?.map((task: Task) => (
                                    <Paper
                                        key={task.id}
                                        withBorder
                                        p="sm"
                                        radius="md"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => onClickTask(task.id)}
                                    >
                                        <Flex justify="space-between" align="flex-start" gap="xs">
                                            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                                {!isPatientDashboard && task.patientName && (
                                                    <Text size="xs" c="dimmed" fw={500}>
                                                        {task.patientName}
                                                    </Text>
                                                )}
                                                <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>
                                                    {task.description}
                                                </Text>
                                                <Flex gap="xs" align="center" wrap="wrap" mt={4}>
                                                    {task.isRecurring && (
                                                        <Badge
                                                            size="xs"
                                                            variant="filled"
                                                            color="blue.9"
                                                            leftSection={<IconRepeat size={10} />}
                                                        >
                                                            {t('badge.recurring', 'Rec.')}
                                                        </Badge>
                                                    )}
                                                    <Badge size="xs" variant="light" color="gray">
                                                        {task.status}
                                                    </Badge>
                                                    {task.dueDate && (
                                                        <Badge
                                                            size="xs"
                                                            variant="outline"
                                                            color="gray"
                                                            leftSection={<IconCalendarDue size={10} />}
                                                        >
                                                            {Utils.formatDateToYYYYMMDD(task.dueDate)}
                                                        </Badge>
                                                    )}
                                                </Flex>
                                            </Stack>
                                            {renderTaskActions(task)}
                                        </Flex>
                                    </Paper>
                                ))}
                                {(!filteredTasks || filteredTasks.length === 0) && (
                                    <Text c="dimmed" ta="center" py="xl">
                                        {t('global.noResults', 'No results')}
                                    </Text>
                                )}
                            </Stack>
                        ) : (
                            /* Desktop/tablet table view */
                            <ScrollArea w="100%">
                            <Table verticalSpacing="xs" style={{ tableLayout: 'auto' }}>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            hidden={isPatientDashboard}
                                            sorted={sortBy === 'patientName'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('patientName', sortedData)}
                                        >
                                            {t('global.patientName')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'description'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('description', sortedData)}
                                        >
                                            {t('table.task.description')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'completedBy'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('completedBy', sortedData)}
                                            hidden={width < 600}
                                        >
                                            {t('table.task.completedBy')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'dateOrdered'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('dateOrdered', sortedData)}
                                            hidden={width < 750}
                                        >
                                            <Flex align="center" gap={4}><IconCalendar size={14} />{t('table.task.dateOrdered')}</Flex>
                                        </Th>

                                        <Th
                                            sorted={sortBy === 'availableDate'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('availableDate', sortedData)}
                                            hidden={width < 800}
                                        >
                                            <Flex align="center" gap={4}><IconCalendarCheck size={14} />{t('table.task.availableDate', 'Available')}</Flex>
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'dueDate'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('dueDate', sortedData)}
                                            hidden={width < 800}
                                        >
                                            <Flex align="center" gap={4}><IconCalendarDue size={14} />{t('table.task.dateDue')}</Flex>
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'status'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('status', sortedData)}
                                        >
                                            {t('global.status')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'lastReminderSentDate'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('lastReminderSentDate', sortedData)}
                                            hidden={width < 900}
                                        >
                                            {t('table.task.lastReminderSent')}
                                        </Th>
                                        <Table.Th style={{ width: 'min-content', position: 'sticky', right: 0, background: 'var(--mantine-color-body)' }}></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData?.map((task: Task, index: number) => {
                                        const isSearch =
                                            containsStringIgnoreCase(
                                                [`${task.patientName}`, `${task.description}`],
                                                search,
                                            ) || !search;
                                        return (
                                            <Table.Tr
                                                key={index}
                                                hidden={!isSearch}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                }}
                                            >
                                                <Table.Td
                                                    hidden={isPatientDashboard}
                                                    style={{
                                                        maxWidth: '100px',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{
                                                            root: {
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis',
                                                            },
                                                        }}
                                                        truncate
                                                    >
                                                        {task.patientName || ''}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td
                                                    style={{
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    <Tooltip
                                                        label={task.description || 'No description'}
                                                        multiline
                                                        withArrow
                                                        position="top"
                                                        disabled={!task.description || task.description.length <= 40}
                                                        styles={{
                                                            tooltip: {
                                                                fontSize: '14px',
                                                                lineHeight: '1.4',
                                                                maxHeight: '200px',
                                                                maxWidth: '300px',
                                                                overflowY: 'auto',
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word'
                                                            }
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            {task.isRecurring && (
                                                                <Tooltip label={t('toolTip.recurringTask', 'Recurring Task')}>
                                                                    <Badge
                                                                        size="xs"
                                                                        variant="filled"
                                                                        color="blue.9"
                                                                        leftSection={<IconRepeat size={12} />}
                                                                        style={{ flexShrink: 0 }}
                                                                    >
                                                                        {t('badge.recurring', 'Rec.')}
                                                                    </Badge>
                                                                </Tooltip>
                                                            )}
                                                            <Highlight
                                                                highlight={search}
                                                                highlightStyles={{
                                                                    color: 'var(--mantine-color-gray-9)',
                                                                    fontWeight: 700,
                                                                    WebkitBackgroundClip: 'text',
                                                                }}
                                                                styles={{
                                                                    root: {
                                                                        fontSize: 14,
                                                                        fontWeight: 500,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }
                                                                }}
                                                            >
                                                                {task.description || ''}
                                                            </Highlight>
                                                        </div>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td hidden={width < 600}>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {task.completedBy || ''}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td hidden={width < 750}>
                                                    {Utils.formatDateToYYYYMMDD(task.dateOrdered!)}
                                                </Table.Td>
                                                <Table.Td hidden={width < 800}>
                                                    {Utils.formatDateToYYYYMMDD(task.availableDate || undefined)}
                                                </Table.Td>
                                                <Table.Td hidden={width < 800}>
                                                    {Utils.formatDateToYYYYMMDD(task.dueDate || undefined)}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Tooltip label={task.statusDetail}>
                                                        <Text fw={500} size={'sm'}>
                                                            {task.status}
                                                        </Text>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td hidden={width < 900}>
                                                    {task.lastReminderSentDate ? (
                                                        <Tooltip
                                                            label={task.lastReminderSentBy || t('table.task.systemGenerated')}
                                                            withArrow
                                                        >
                                                            <Text fw={500} size={'sm'} style={{ cursor: 'default' }}>
                                                                {Utils.formatDateToYYYYMMDD(task.lastReminderSentDate)}
                                                            </Text>
                                                        </Tooltip>
                                                    ) : (
                                                        <Text fw={500} size={'sm'} c="dimmed">
                                                            —
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                                <Table.Td style={{
                                                    width: 'min-content',
                                                    position: 'sticky',
                                                    right: 0,
                                                    background: index % 2 === 1 ? 'var(--mantine-color-body)' : 'var(--Background-Gray-light, #E9ECEF)',
                                                }}>
                                                    {renderTaskActions(task)}
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        );
    }),
);

const WrappedTaskTable: React.FC = () => {
    return (
        <TableContextProvider>
            <TaskTable />
        </TableContextProvider>
    );
};

export default WrappedTaskTable;
